import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Phone,
  Email,
  Note,
  Event,
  TrendingUp,
} from '@mui/icons-material';
import { repAPI } from '../../services/api';
import LeadEditDialog from '../../components/rep/LeadEditDialog';
import LeadTimeline from '../../components/rep/LeadTimeline';
import LeadContacts from '../../components/rep/LeadContacts';
import LeadLoginRequests from '../../components/rep/LeadLoginRequests';

const LeadDetailPage = ({ currentUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    fetchLead();
  }, [id]);

  const fetchLead = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await repAPI.getLead(id);
      if (response.success) {
        setLead(response.data);
      } else {
        setError(response.error || 'Failed to load lead');
      }
    } catch (err) {
      setError(err.message || 'Failed to load lead');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePushToAdmin = async () => {
    if (!window.confirm('Push this lead to admin for approval? Make sure all required fields are filled.')) {
      return;
    }

    try {
      setError('');
      await repAPI.pushLeadToAdmin(id);
      setSuccess('Lead pushed to admin for approval');
      fetchLead(); // Refresh to get updated status
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to push lead');
    }
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    fetchLead(); // Refresh lead data
    setSuccess('Lead updated successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleActivityAdded = () => {
    fetchLead(); // Refresh to show new activity/note
  };

  const handleContactUpdated = () => {
    fetchLead(); // Refresh to show updated contacts
  };

  const getStatusColor = (status) => {
    const colors = {
      NEW: 'default',
      CONTACTED: 'info',
      QUALIFIED: 'primary',
      PROPOSAL_SENT: 'secondary',
      PENDING_ADMIN_APPROVAL: 'warning',
      CLOSED_WON: 'success',
      CLOSED_LOST: 'error',
      CONVERTED_TO_CLIENT: 'success',
    };
    return colors[status] || 'default';
  };

  const formatStatus = (status) => {
    return status
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatSource = (source) => {
    return source
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !lead) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/rep/crm-leads')}>
          Back to Leads
        </Button>
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">Lead not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/rep/crm-leads')} sx={{ mt: 2 }}>
          Back to Leads
        </Button>
      </Box>
    );
  }

  const canPushToAdmin = 
    lead.status !== 'PENDING_ADMIN_APPROVAL' && 
    lead.status !== 'CONVERTED_TO_CLIENT';

  return (
    <Box
      sx={{
        pt: { xs: 7, md: 3 },
        minHeight: '100vh',
        maxWidth: '1600px',
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Top Bar */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/rep/crm-leads')}>
            <ArrowBack />
          </IconButton>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: '#212121',
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            {lead.clinicName}
          </Typography>
          <Chip 
            label={formatStatus(lead.status)} 
            color={getStatusColor(lead.status)} 
            size="small"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => setEditDialogOpen(true)}
          >
            Edit Lead
          </Button>
          {canPushToAdmin && (
            <Button
              variant="contained"
              color="primary"
              onClick={handlePushToAdmin}
            >
              Push to Admin
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column: Lead Details */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
              Lead Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Clinic Name
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}>
                  {lead.clinicName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Contact Name
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}>
                  {lead.contactName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Email
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {lead.email}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {lead.phone || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Address
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {lead.address || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  City
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {lead.city || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  State
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {lead.state || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Zip Code
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {lead.zipCode || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip 
                    label={formatStatus(lead.status)} 
                    color={getStatusColor(lead.status)} 
                    size="small"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Priority
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip 
                    label={lead.priority === 1 ? 'High' : lead.priority === 2 ? 'Medium' : 'Low'} 
                    color={lead.priority === 1 ? 'error' : lead.priority === 2 ? 'warning' : 'default'} 
                    size="small"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Source
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif', mt: 0.5 }}>
                  {formatSource(lead.source)}
                </Typography>
              </Grid>

              {(lead.estimatedAnnualVolume || lead.numberOfProviders || lead.currentSupplier || lead.interestedProducts) && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  {lead.estimatedAnnualVolume && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        Estimated Annual Volume
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}>
                        {lead.estimatedAnnualVolume.toLocaleString()}
                      </Typography>
                    </Grid>
                  )}

                  {lead.numberOfProviders && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        Number of Providers
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}>
                        {lead.numberOfProviders}
                      </Typography>
                    </Grid>
                  )}

                  {lead.currentSupplier && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        Current Supplier
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {lead.currentSupplier}
                      </Typography>
                    </Grid>
                  )}

                  {lead.interestedProducts && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        Interested Products
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {lead.interestedProducts}
                      </Typography>
                    </Grid>
                  )}

                  {lead.expectedCloseDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        Expected Close Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {new Date(lead.expectedCloseDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  )}

                  {lead.estimatedValue && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        Estimated Value
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}>
                        ${parseFloat(lead.estimatedValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Created
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {new Date(lead.createdAt).toLocaleString()}
                </Typography>
              </Grid>

              {lead.lastContactedAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Last Contacted
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    {new Date(lead.lastContactedAt).toLocaleString()}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Contacts Section */}
          <Box sx={{ mt: 3 }}>
            <LeadContacts lead={lead} onContactUpdated={handleContactUpdated} />
          </Box>

          {/* Login Requests Section */}
          <Box sx={{ mt: 3 }}>
            <LeadLoginRequests 
              lead={lead} 
              onRequestUpdated={handleContactUpdated}
              isRep={true}
            />
          </Box>
        </Grid>

        {/* Right Column: Timeline */}
        <Grid item xs={12} md={5}>
          <LeadTimeline 
            lead={lead} 
            onActivityAdded={handleActivityAdded}
          />
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <LeadEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        lead={lead}
        onSuccess={handleEditSuccess}
      />
    </Box>
  );
};

export default LeadDetailPage;

